import { ensureSDK } from '../sdk.js';

const BaseHTMLElement =
  typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

function parseIds(raw) {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (_e) {}
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function readIntentData(element) {
  const data = {};
  const productId = element.getAttribute('product-id');
  const productIds = element.getAttribute('product-ids');
  const categoryId = element.getAttribute('category-id');

  if (productIds) {
    data.productIds = parseIds(productIds);
  } else if (productId) {
    data.productIds = [productId];
  }

  if (categoryId) {
    data.categoryIds = [categoryId];
  }

  const tag = element.getAttribute('tag');
  if (tag) {
    data.tag = tag;
  }

  const sourceLocation = element.getAttribute('source-location');
  if (sourceLocation) {
    data.sourceLocation = sourceLocation;
  }

  const eventName = element.getAttribute('event-name');
  if (eventName) {
    element.dataset.eventName = eventName;
  }

  return data;
}

function mergeIntentData(element, patch) {
  let existing = {};
  try {
    existing = JSON.parse(element.getAttribute('data-buyer-intent') || '{}');
  } catch (_e) {
    existing = {};
  }
  const merged = { ...existing, ...patch };
  element.setAttribute('data-buyer-intent', JSON.stringify(merged));
}

function defineElement(tagName, ElementClass) {
  if (typeof customElements !== 'undefined' && !customElements.get(tagName)) {
    customElements.define(tagName, ElementClass);
  }
}

class BuyerIntentSessionElement extends BaseHTMLElement {
  connectedCallback() {
    this.style.display = 'contents';
  }
}

class BuyerIntentSubjectElement extends BaseHTMLElement {
  connectedCallback() {
    this.style.display = 'none';
  }
}

class BuyerIntentViewElement extends BaseHTMLElement {
  connectedCallback() {
    ensureSDK();
    mergeIntentData(this, readIntentData(this));
    this.dataset.trigger = 'view';

    const hasOwnProductIds = () => {
      if (this.getAttribute('product-id') || this.getAttribute('product-ids')) {
        return true;
      }

      try {
        const parsed = JSON.parse(
          this.getAttribute('data-buyer-intent') || '{}'
        );
        return Array.isArray(parsed.productIds) && parsed.productIds.length > 0;
      } catch (_error) {
        return false;
      }
    };

    const hasSubjectProductIds = () => {
      if (typeof this.querySelectorAll !== 'function') {
        return false;
      }

      const subjects = this.querySelectorAll('buyer-intent-subject');
      for (const subject of subjects) {
        const pids = subject.getAttribute('product-ids');
        const pid = subject.getAttribute('product-id');
        if (pid) {
          return true;
        }
        if (pids && parseIds(pids).length > 0) {
          return true;
        }
      }
      return false;
    };

    // Defer view tracking until descendants are connected and readable.
    const dispatchView = () => {
      this.dispatchEvent(
        new CustomEvent('buyerintent:view', { bubbles: true })
      );
    };

    queueMicrotask(() => {
      const waitForSubjects = !hasOwnProductIds();
      const startTime = Date.now();
      const maxWaitMs = 1500;

      const checkAndDispatch = () => {
        if (!this.isConnected) {
          return;
        }

        if (!waitForSubjects || hasSubjectProductIds()) {
          dispatchView();
          return;
        }

        if (Date.now() - startTime >= maxWaitMs) {
          dispatchView();
          return;
        }

        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(checkAndDispatch);
          return;
        }
        setTimeout(checkAndDispatch, 16);
      };

      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(checkAndDispatch);
        return;
      }
      setTimeout(checkAndDispatch, 0);
    });
  }
}

class BuyerIntentClickElement extends BaseHTMLElement {
  connectedCallback() {
    ensureSDK();
    mergeIntentData(this, readIntentData(this));
    this.dataset.trigger = 'click';
    this._handleClick = () => {
      this.dispatchEvent(
        new CustomEvent('buyerintent:click', { bubbles: true })
      );
    };
    // Capture clicks before descendants can stop propagation. Several CTA
    // surfaces intentionally stop bubbling to avoid parent navigation, but we
    // still need the buyer-intent wrapper to observe the click.
    this.addEventListener('click', this._handleClick, true);
  }

  disconnectedCallback() {
    if (this._handleClick) {
      this.removeEventListener('click', this._handleClick, true);
      this._handleClick = null;
    }
  }
}

defineElement('buyer-intent-session', BuyerIntentSessionElement);
defineElement('buyer-intent-subject', BuyerIntentSubjectElement);
defineElement('buyer-intent-view', BuyerIntentViewElement);
defineElement('buyer-intent-click', BuyerIntentClickElement);

export {
  BuyerIntentSessionElement,
  BuyerIntentSubjectElement,
  BuyerIntentViewElement,
  BuyerIntentClickElement,
  mergeIntentData,
  defineElement,
};
